import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {
  fetchIssueProjects,
  issueProjectsQueryKey,
  projectsAppliedToAll,
} from '@github-ui/issues-bulk-actions/fetch-issue-projects'
import {useQueryClient} from '@github-ui/react-query'
import {ProjectSymlinkIcon} from '@primer/octicons-react'
import {useCallback, useState} from 'react'

import {RepoIssuesBulkPickerAnchor, type RepoIssuesBulkActionProps} from './RepoIssuesBulkActionShared'

export function RepoIssuesBulkProjects({
  issueIds,
  ...props
}: RepoIssuesBulkActionProps & {issueIds: readonly string[]}) {
  const selectionKey = JSON.stringify([...issueIds].sort())
  return <RepoIssuesBulkProjectsForSelection key={selectionKey} issueIds={issueIds} {...props} />
}

function RepoIssuesBulkProjectsForSelection({
  disabled,
  nested,
  owner,
  repo,
  issueIds,
  onUpdate,
}: RepoIssuesBulkActionProps & {issueIds: readonly string[]}) {
  const queryClient = useQueryClient()
  const [selectedProjects, setSelectedProjects] = useState<ProjectPickerBaseProject[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [queryError, setQueryError] = useState<Error | null>(null)
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => (
      <RepoIssuesBulkPickerAnchor
        {...{anchorProps, disabled: disabled || isLoading, nested}}
        icon={ProjectSymlinkIcon}
        label="Project"
      />
    ),
    [disabled, isLoading, nested],
  )
  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('projects'),
    anchorElement,
    open: true,
  })
  const loadProjects = useCallback(async () => {
    if (disabled || isLoading) return
    setIsLoading(true)
    try {
      const data = await queryClient.fetchQuery({
        queryKey: issueProjectsQueryKey(issueIds),
        queryFn: ({signal}) => fetchIssueProjects(issueIds, signal),
      })
      setSelectedProjects(projectsAppliedToAll(data))
    } catch (error) {
      setQueryError(error instanceof Error ? error : new Error('Failed to load projects'))
    } finally {
      setIsLoading(false)
    }
  }, [disabled, isLoading, issueIds, queryClient])

  if (queryError) {
    return createFallbackComponent(() => {
      setQueryError(null)
      void loadProjects()
    })
  }

  if (!selectedProjects) {
    return anchorElement({
      onClick: () => {
        void loadProjects()
      },
    })
  }

  return (
    <ProjectPicker
      pickerId="repo-issues-bulk-projects"
      anchorElement={anchorElement}
      title="Add to projects"
      repo={repo}
      owner={owner}
      readonly={disabled}
      selectedProjects={selectedProjects}
      onSave={(projects: ProjectPickerBaseProject[]) => {
        const addToProjectIds = projects
          .filter(project => !selectedProjects.some(selected => selected.id === project.id))
          .map(project => project.id)
        const removeFromProjectIds = selectedProjects
          .filter(project => !projects.some(selected => selected.id === project.id))
          .map(project => project.id)
        if (addToProjectIds.length > 0 || removeFromProjectIds.length > 0) {
          onUpdate({addToProjectIds, removeFromProjectIds})
        }
      }}
      nested={nested}
      triggerOpen
    />
  )
}
